import React, { useEffect, useState } from 'react';
import api from 'helpers/api';
import { useAngularService } from 'helpers/get_angular_service';
import Button from '../../components/button';
import { Gym } from '../../types/gym';
import Input from '../../components/input';
import Label from '../../components/label';
import { useExtendState } from 'helpers/use_extend_state';
import notification from 'helpers/notification';
import emailValidator from 'helpers/email_validator';
import replaceAll from 'helpers/replace_all';
import ErrorMessage from '../../components/error_message';
import Link from '../../components/link';
import { useResponsive } from 'helpers/use_responsive';

interface Params {
  email: string;
  code: string;
  new_code: string;
  status?: 'allowed' | 'disallowed_multisite' | 'disallowed_too_many_changes';
  state: 'initial' | 'verified';
  email_change_limit?: number;
  reference?: string;
  error?: string;
}

const ChangeEmailModal = ({ modalInstance, profile }) => {
  const $rootScope = useAngularService('$rootScope');
  const gym: Gym = $rootScope.gym;
  const [params, extendParams] = useExtendState<Params>({
    email: '',
    code: '',
    new_code: '',
    state: 'initial',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [userIdParam] = useState(() =>
    profile.id === window.currentUser.id ? {} : { user_id: profile.id },
  );

  const { isMobile } = useResponsive();

  const getEmailChangeStatus = () => {
    const url = `/gyms/${gym.id}/profile/email_change_allowed`;
    api.get(url, userIdParam).then(
      ({ status, email_change_limit }) => {
        extendParams({ status, email_change_limit });
      },
      () => {
        notification.error('Request could not be completed. Please try again');
      },
    );
  };

  useEffect(() => {
    getEmailChangeStatus();
  }, []);

  const requestEmailChange = () => {
    const url = `/gyms/${gym.id}/profile/request_email_change`;
    setIsLoading(true);
    extendParams({ error: '' });
    api.post(url, { new_email: params.email, ...userIdParam }).then(
      ({ email_change_reference }) => {
        setIsLoading(false);
        extendParams({ state: 'verified', reference: email_change_reference });
      },
      ({ data }) => {
        extendParams({ error: data.error });
        setIsLoading(false);
      },
    );
  };

  const updateEmail = () => {
    const url = `/gyms/${gym.id}/profile/update_email`;
    setIsLoading(true);
    extendParams({ error: '' });
    api
      .post(url, {
        code: params.code,
        new_code: params.new_code,
        reference: params.reference,
        ...userIdParam,
      })
      .then(
        (response) => {
          notification.success('Email changed successfully');
          modalInstance.close(response);
        },
        ({ data }) => {
          extendParams({ error: data.error });
          setIsLoading(false);
        },
      );
  };

  const cancel = () => {
    modalInstance.dismiss('cancel');
  };

  if (!params.status) return null;

  return (
    <div>
      {params.status === 'allowed' && (
        <>
          <div className="modal-header">
            <a aria-hidden="true" className="close-modal" onClick={cancel} title="Close">
              <i className="icon-cross"></i>
            </a>
            <h4 className="modal-title">Change user email address</h4>
          </div>
          <div className="modal-body">
            <div>
              User: <strong>{profile.name}</strong>
            </div>
            <div>
              Current email: <strong>{profile.email}</strong>
            </div>
            <p>
              Verification will be required before this change is made. Please provide the new email
              address:
            </p>

            <div>
              <Label>New email:</Label>
              <div className="flex">
                <Input
                  type="email"
                  value={params.email}
                  onChange={(email: string) => extendParams({ email })}
                  disabled={params.state !== 'initial'}
                />
                {params.state === 'initial' && (
                  <div className="margin-left">
                    <Button
                      variant="primary"
                      disabled={isLoading || !emailValidator(params.email)}
                      onClick={requestEmailChange}
                    >
                      Verify
                    </Button>
                  </div>
                )}
              </div>

              {params.state === 'initial' && <ErrorMessage error={params.error} />}
            </div>

            {params.state === 'verified' && (
              <>
                <p>
                  Verification codes{' '}
                  {!isMobile
                    ? `have been sent to both ${profile.email} and ${params.email}. `
                    : 'have been sent to both your old and new email addresses. '}
                  Please enter both codes below to continue:
                </p>
                <div className="flex space-between">
                  <div className="flex">
                    <div>
                      <Label className="control-label">Verification code (old email):</Label>
                      <Input
                        value={params.code}
                        disabled={isLoading}
                        onChange={(code: string) =>
                          extendParams({ code: replaceAll(code, ' ', '') })
                        }
                      />
                    </div>

                    <div className="margin-left">
                      <Label className="control-label">Verification code (new email):</Label>
                      <Input
                        value={params.new_code}
                        disabled={isLoading}
                        onChange={(new_code: string) =>
                          extendParams({ new_code: replaceAll(new_code, ' ', '') })
                        }
                      />
                    </div>
                  </div>

                  <div className="email-verified-group-item-top margin-left">
                    <Button
                      variant="primary"
                      disabled={isLoading || !params.code || !params.new_code}
                      onClick={updateEmail}
                    >
                      Confirm
                    </Button>
                  </div>
                </div>
                <div className="margin-bottom">
                  <ErrorMessage error={params.error} />
                </div>
              </>
            )}
          </div>
        </>
      )}

      {params.status === 'disallowed_multisite' && (
        <>
          <div className="modal-header">
            <a aria-hidden="true" className="close-modal" onClick={cancel} title="Close">
              <i className="icon-cross"></i>
            </a>
            <h4 className="modal-title">Email change disallowed</h4>
          </div>
          <div className="modal-body">
            <p>This user belongs to at least one other Clubworx account.</p>
            <p>
              To update this user's email address, the change must be made by the user themselves.{' '}
              <strong>
                However, please be aware that an email change will apply to ALL Clubworx accounts
                the user belongs to.
              </strong>
            </p>
            <p>
              If you are trying to manage this user's access to your Clubworx account, consider
              using the "Block access" option from the{' '}
              <Link route="gym.users" target="_self">
                Manage Users page
              </Link>
              .
            </p>
          </div>
        </>
      )}

      {params.status === 'disallowed_too_many_changes' && (
        <>
          <div className="modal-header">
            <a aria-hidden="true" className="close-modal" onClick={cancel} title="Close">
              <i className="icon-cross"></i>
            </a>
            <h4 className="modal-title">Email change disallowed</h4>
          </div>
          <div className="modal-body">
            <p>
              This user's email address has already been changed{' '}
              <strong>{params.email_change_limit} times</strong>.
            </p>
            <p>If you'd like to continue with this change, please contact Clubworx support.</p>
          </div>
        </>
      )}
    </div>
  );
};

angular.module('gym').value('ChangeEmailModal', ChangeEmailModal);
